/* PatientsImport — iter101.18
 *
 * Bulk-import wizard for doctors migrating an existing patient roster
 * into Invisique. Two-phase flow:
 *   1) Upload CSV → server returns a per-row preview (valid / duplicate /
 *      cross_doctor / invalid) with an explanatory note.
 *   2) Doctor reviews the amber/green/red table, then clicks "Import N".
 *      Server re-validates and writes only the valid rows in one batch.
 *
 * Template + history live under the same page so the doctor never has to
 * bounce out to find the sample CSV or check an earlier batch.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
    ArrowLeft, UploadSimple, DownloadSimple, FileCsv, CheckCircle,
    Warning, WarningOctagon, Users, ArrowClockwise, ClockCounterClockwise,
    Info,
} from "@phosphor-icons/react";
import { confirmDialog } from "@/lib/confirm";


function fmtDate(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
        return iso;
    }
}


function StatusPill({ status }) {
    const map = {
        valid:        { cls: "bg-emerald-50 text-emerald-800 ring-emerald-200", label: "Ready", Icon: CheckCircle },
        duplicate:    { cls: "bg-slate-100 text-slate-700 ring-slate-200",       label: "Already yours", Icon: Info },
        cross_doctor: { cls: "bg-amber-50 text-amber-800 ring-amber-200",         label: "Other doctor", Icon: Warning },
        invalid:      { cls: "bg-rose-50 text-rose-800 ring-rose-200",             label: "Fix needed", Icon: WarningOctagon },
    };
    const m = map[status] || map.invalid;
    const { Icon } = m;
    return (
        <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ring-1 text-[10px] font-bold tracking-wide uppercase ${m.cls}`}
            data-testid={`import-row-status-${status}`}
        >
            <Icon size={10} weight="fill" />
            {m.label}
        </span>
    );
}


function StatTile({ icon: Icon, label, value, tone = "neutral", testid }) {
    const palette = {
        neutral:  "bg-secondary/40 text-foreground ring-border",
        emerald:  "bg-emerald-50 text-emerald-900 ring-emerald-200",
        amber:    "bg-amber-50 text-amber-900 ring-amber-200",
        rose:     "bg-rose-50 text-rose-900 ring-rose-200",
        slate:    "bg-slate-50 text-slate-900 ring-slate-200",
    };
    return (
        <div className={`rounded-xl ring-1 px-4 py-3 ${palette[tone]}`} data-testid={testid}>
            <div className="flex items-center gap-2 mb-1">
                <Icon size={13} weight="fill" className="opacity-70" />
                <span className="text-[10px] font-bold uppercase tracking-wider opacity-80">{label}</span>
            </div>
            <div className="text-2xl font-display font-bold tabular-nums">{value}</div>
        </div>
    );
}


export default function PatientsImport() {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [preview, setPreview] = useState(null); // {rows, summary, file_name}
    const [committing, setCommitting] = useState(false);
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(true);

    const loadHistory = async () => {
        setHistoryLoading(true);
        try {
            const r = await api.get("/doctor/patients/import/history");
            setHistory(r.data?.batches || []);
        } catch (e) {
            // Silent — the history strip is optional.
        } finally {
            setHistoryLoading(false);
        }
    };
    useEffect(() => { loadHistory(); }, []);

    const downloadTemplate = async () => {
        try {
            const res = await api.get("/doctor/patients/import/template", { responseType: "blob" });
            const blob = new Blob([res.data], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "invisique-patient-import-template.csv";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Template downloaded — fill it in and re-upload");
        } catch (e) {
            toast.error("Couldn't download the template");
        }
    };

    const uploadFile = async (file) => {
        if (!file) return;
        if (!/\.(csv|txt)$/i.test(file.name)) {
            toast.error("Please upload a .csv file (save from Excel via 'Save As CSV')");
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            toast.error("File too large — max 2MB (about 20,000 rows)");
            return;
        }
        setUploading(true);
        setPreview(null);
        try {
            const form = new FormData();
            form.append("file", file);
            const r = await api.post("/doctor/patients/import/preview", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPreview(r.data);
            if (r.data.summary?.valid > 0) {
                toast.success(`${r.data.summary.valid} of ${r.data.summary.total} rows ready to import`);
            } else {
                toast.warning("No importable rows found — please review below");
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't parse this CSV");
        } finally {
            setUploading(false);
        }
    };

    const onFileChange = (e) => {
        const f = e.target.files?.[0];
        if (f) uploadFile(f);
        e.target.value = "";
    };
    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer?.files?.[0];
        if (f) uploadFile(f);
    };

    const doCommit = async () => {
        if (!preview || !preview.summary || preview.summary.valid < 1) return;
        const ok = await confirmDialog({
            title: `Import ${preview.summary.valid} patients into your list?`,
            body: "Rows marked 'Already yours' or 'Other doctor' will be skipped. You can rollback this batch anytime in the next 30 days from the history strip below.",
            confirmText: `Import ${preview.summary.valid}`,
        });
        if (!ok) return;
        setCommitting(true);
        try {
            const r = await api.post("/doctor/patients/import/commit", {
                rows: preview.rows,
                file_name: preview.file_name || "",
            });
            toast.success(`Imported ${r.data.imported_count} patients`);
            setPreview(null);
            loadHistory();
            // Nudge back to the roster so they immediately see their new list
            setTimeout(() => navigate("/doctor/patients"), 600);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Import failed");
        } finally {
            setCommitting(false);
        }
    };

    const rollback = async (batch) => {
        const ok = await confirmDialog({
            title: `Rollback the batch of ${batch.imported_count} patients?`,
            body: "Only patients with zero aligner cases will be removed. Patients who already have a case stay put — the batch record marks them as skipped.",
            confirmText: "Rollback",
            tone: "danger",
        });
        if (!ok) return;
        try {
            const r = await api.post(`/doctor/patients/import/${batch.id}/rollback`);
            toast.success(`Rolled back ${r.data.rolled_back} of ${batch.imported_count} patients`);
            loadHistory();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't rollback this batch");
        }
    };

    const s = preview?.summary || { total: 0, valid: 0, duplicate: 0, cross_doctor: 0, invalid: 0 };

    return (
        <div className="space-y-6" data-testid="patients-import-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                    <Link to="/doctor/patients" className="text-[11px] text-muted-foreground hover:text-primary inline-flex items-center gap-1">
                        <ArrowLeft size={11} weight="bold" /> Back to My Patients
                    </Link>
                    <h1 className="mt-1 font-display text-2xl font-bold tracking-tight">Import patients</h1>
                    <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                        Bring an existing patient roster into Invisique in one go. Works with any CSV — the template
                        locks the column names, so grab a copy first, paste your rows in, and re-upload.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full text-xs"
                        onClick={downloadTemplate}
                        data-testid="import-template-btn"
                    >
                        <DownloadSimple size={13} className="mr-1" /> Download template
                    </Button>
                </div>
            </div>

            {/* Drop zone */}
            {!preview && (
                <div
                    className={`rounded-2xl border-2 border-dashed transition-all p-8 sm:p-12 text-center ${dragOver ? "border-primary bg-primary/5" : "border-border bg-secondary/25 hover:border-foreground/25"}`}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    data-testid="import-dropzone"
                >
                    <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 grid place-items-center mb-3">
                        <UploadSimple size={26} className="text-primary" weight="duotone" />
                    </div>
                    <div className="font-display text-lg font-semibold text-foreground">Drop your CSV here</div>
                    <div className="text-xs text-muted-foreground mt-1">Or click to browse — up to 2MB / 1,000 rows per file</div>
                    <div className="mt-4">
                        <Button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            className="rounded-full"
                            data-testid="import-pick-file-btn"
                        >
                            {uploading ? <ArrowClockwise size={14} className="animate-spin mr-2" weight="bold" /> : <FileCsv size={14} className="mr-2" weight="fill" />}
                            {uploading ? "Reading CSV…" : "Choose CSV file"}
                        </Button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,text/csv"
                            className="hidden"
                            onChange={onFileChange}
                            data-testid="import-file-input"
                        />
                    </div>
                    <div className="mt-6 text-[11px] text-muted-foreground max-w-lg mx-auto leading-relaxed">
                        Columns required: <code className="font-mono text-foreground/80">name</code>, <code className="font-mono text-foreground/80">phone</code>.
                        Optional: <code className="font-mono text-foreground/80">email</code>, <code className="font-mono text-foreground/80">age</code>, <code className="font-mono text-foreground/80">gender</code>, <code className="font-mono text-foreground/80">notes</code>, <code className="font-mono text-foreground/80">tags</code> (comma-separated).
                    </div>
                </div>
            )}

            {/* Preview */}
            {preview && (
                <>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                        <StatTile icon={Users}          label="Total rows"   value={s.total}         tone="neutral" testid="import-stat-total" />
                        <StatTile icon={CheckCircle}    label="Ready"         value={s.valid}         tone="emerald" testid="import-stat-valid" />
                        <StatTile icon={Info}           label="Already yours" value={s.duplicate}     tone="slate"   testid="import-stat-duplicate" />
                        <StatTile icon={Warning}        label="Other doctor"  value={s.cross_doctor}  tone="amber"   testid="import-stat-cross" />
                        <StatTile icon={WarningOctagon} label="Fix needed"    value={s.invalid}       tone="rose"    testid="import-stat-invalid" />
                    </div>

                    <div className="rounded-xl border border-border bg-card overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-secondary/30">
                            <div className="min-w-0">
                                <div className="text-[13px] font-semibold text-foreground truncate">{preview.file_name || "Preview"}</div>
                                <div className="text-[11px] text-muted-foreground">
                                    {s.valid > 0
                                        ? `${s.valid} row${s.valid === 1 ? "" : "s"} ready to import. Skipped rows stay in your CSV — fix and re-upload if needed.`
                                        : "Nothing importable yet — review the notes column and fix the invalid rows in your source spreadsheet."}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => { setPreview(null); }}
                                    className="rounded-full text-xs"
                                    data-testid="import-cancel-btn"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={doCommit}
                                    disabled={committing || s.valid < 1}
                                    className="rounded-full"
                                    data-testid="import-commit-btn"
                                >
                                    {committing ? "Importing…" : `Import ${s.valid}`}
                                </Button>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-[12.5px]">
                                <thead className="text-[10px] uppercase tracking-wider text-muted-foreground bg-secondary/40">
                                    <tr>
                                        <th className="text-left px-3 py-2 w-10">#</th>
                                        <th className="text-left px-3 py-2">Name</th>
                                        <th className="text-left px-3 py-2">Phone</th>
                                        <th className="text-left px-3 py-2">Email</th>
                                        <th className="text-left px-3 py-2 w-16">Age</th>
                                        <th className="text-left px-3 py-2 w-24">Gender</th>
                                        <th className="text-left px-3 py-2">Tags</th>
                                        <th className="text-left px-3 py-2 w-28">Status</th>
                                        <th className="text-left px-3 py-2">Note</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {preview.rows.map((row) => (
                                        <tr
                                            key={row.index}
                                            className={`border-t border-border/60 ${row.status === "valid" ? "" : "bg-secondary/20"}`}
                                            data-testid={`import-row-${row.index}`}
                                        >
                                            <td className="px-3 py-2 text-[11px] text-muted-foreground tabular-nums">{row.index}</td>
                                            <td className="px-3 py-2 font-medium text-foreground truncate max-w-[180px]">{row.name || "—"}</td>
                                            <td className="px-3 py-2 tabular-nums">{row.phone || "—"}</td>
                                            <td className="px-3 py-2 text-muted-foreground truncate max-w-[180px]">{row.email || "—"}</td>
                                            <td className="px-3 py-2 tabular-nums">{row.age || "—"}</td>
                                            <td className="px-3 py-2">{row.gender || "—"}</td>
                                            <td className="px-3 py-2">
                                                {(row.tags || []).length > 0 ? (
                                                    <div className="flex flex-wrap gap-1">
                                                        {(row.tags || []).map((t) => (
                                                            <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary">{t}</span>
                                                        ))}
                                                    </div>
                                                ) : "—"}
                                            </td>
                                            <td className="px-3 py-2"><StatusPill status={row.status} /></td>
                                            <td className="px-3 py-2 text-[11.5px] text-muted-foreground">{row.note}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            {/* History */}
            {(historyLoading || history.length > 0) && (
                <div className="rounded-xl border border-border bg-card overflow-hidden">
                    <div className="px-4 py-3 border-b border-border bg-secondary/25">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold flex items-center gap-1.5">
                            <ClockCounterClockwise size={11} weight="fill" /> Recent imports
                        </div>
                    </div>
                    <div className="divide-y divide-border/60">
                        {historyLoading && (
                            <div className="px-4 py-3 text-xs text-muted-foreground">Loading history…</div>
                        )}
                        {!historyLoading && history.map((b) => (
                            <div key={b.id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-testid={`import-history-${b.id}`}>
                                <div className="min-w-0">
                                    <div className="text-[13px] font-semibold text-foreground truncate">
                                        {b.file_name || "Unnamed batch"}
                                        {b.status === "rolled_back" && (
                                            <span className="ml-2 text-[10px] uppercase tracking-wider text-rose-700 bg-rose-50 ring-1 ring-rose-200 px-1.5 py-0.5 rounded-full">
                                                Rolled back
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-[11px] text-muted-foreground mt-0.5">
                                        {fmtDate(b.created_at)} · Imported <span className="tabular-nums font-medium text-foreground">{b.imported_count}</span>
                                        {b.duplicate_count ? ` · Skipped ${b.duplicate_count} duplicate${b.duplicate_count === 1 ? "" : "s"}` : ""}
                                        {b.invalid_count ? ` · ${b.invalid_count} invalid` : ""}
                                        {b.status === "rolled_back" ? ` · Reverted ${b.rolled_back_count || 0}` : ""}
                                    </div>
                                </div>
                                {b.status === "committed" && b.imported_count > 0 && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="rounded-full text-xs"
                                        onClick={() => rollback(b)}
                                        data-testid={`import-rollback-${b.id}`}
                                    >
                                        Rollback
                                    </Button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
